import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ensureRuntimeSession } from "@/features/companion/lib/agentRuntime";

/** Lightweight entity reference for focus tracking. */
export interface FocusEntity {
  id: string;
  name: string;
}

/** Tracks what the user is currently viewing in the content-studio workspace. */
export interface ContentStudioFocusState {
  activeTab: "overview" | "articles" | "sources" | "flows";
  currentArticle?: FocusEntity & { status?: string; sourceId?: string };
  currentSource?: FocusEntity;
  currentFlow?: FocusEntity;
  recentEntities: FocusEntity[];
  activeFilters?: { statusGroup?: string; sourceId?: string; search?: string };
}

interface NovaState {
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;

  /**
   * AgentRuntimeSession UUID for (user, workspace, "nova"). Resolved
   * eagerly by NovaCompanionPanel via ensureRuntimeSession on workspace
   * mount. Persisted across reloads but always re-bootstrapped on
   * mount so a workspace switch picks up the right session.
   */
  activeNovaSessionId: string | null;
  setActiveNovaSession: (sessionId: string | null) => void;

  /** Content Studio focus state — sent with each chat message for context awareness. */
  focusState: ContentStudioFocusState;
  setFocusTab: (tab: ContentStudioFocusState["activeTab"]) => void;
  setFocusArticle: (article: ContentStudioFocusState["currentArticle"]) => void;
  setFocusSource: (source: ContentStudioFocusState["currentSource"]) => void;
  setFocusFlow: (flow: ContentStudioFocusState["currentFlow"]) => void;
  setFocusFilters: (filters: ContentStudioFocusState["activeFilters"]) => void;

  /**
   * Idempotent: returns the unified AgentRuntimeSession UUID for the
   * caller's (user, workspace, "nova") triple. Repeats are no-ops.
   * `workspaceName` is a label used only for in-memory display state
   * — the unified session has no separate name field.
   */
  ensureNovaSession: (workspaceId: string, workspaceName?: string) => Promise<string | null>;
}

export const useNovaStore = create<NovaState>()(
  persist(
    (set) => ({
      panelOpen: false,
      setPanelOpen: (open) => set({ panelOpen: open }),
      togglePanel: () => set((s) => ({ panelOpen: !s.panelOpen })),

      activeNovaSessionId: null,
      setActiveNovaSession: (sessionId) => set({ activeNovaSessionId: sessionId }),

      // ── Focus state ───────────────────────────────────────────────────
      focusState: { activeTab: "overview", recentEntities: [] },
      setFocusTab: (tab) =>
        set((s) => ({ focusState: { ...s.focusState, activeTab: tab } })),
      setFocusArticle: (article) =>
        set((s) => ({
          focusState: {
            ...s.focusState,
            currentArticle: article,
            recentEntities: article
              ? [
                  { id: article.id, name: article.name },
                  ...s.focusState.recentEntities.filter((e) => e.id !== article.id),
                ].slice(0, 5)
              : s.focusState.recentEntities,
          },
        })),
      setFocusSource: (source) =>
        set((s) => ({
          focusState: {
            ...s.focusState,
            currentSource: source,
            recentEntities: source
              ? [
                  { id: source.id, name: source.name },
                  ...s.focusState.recentEntities.filter((e) => e.id !== source.id),
                ].slice(0, 5)
              : s.focusState.recentEntities,
          },
        })),
      setFocusFlow: (flow) =>
        set((s) => ({
          focusState: {
            ...s.focusState,
            currentFlow: flow,
            recentEntities: flow
              ? [
                  { id: flow.id, name: flow.name },
                  ...s.focusState.recentEntities.filter((e) => e.id !== flow.id),
                ].slice(0, 5)
              : s.focusState.recentEntities,
          },
        })),
      setFocusFilters: (filters) =>
        set((s) => ({ focusState: { ...s.focusState, activeFilters: filters } })),

      ensureNovaSession: async (workspaceId, _workspaceName) => {
        const sessionId = await ensureRuntimeSession("nova", workspaceId);
        if (!sessionId) return null;
        set({ activeNovaSessionId: sessionId });
        return sessionId;
      },
    }),
    {
      name: "nova-store",
      partialize: (state) => ({
        panelOpen: state.panelOpen,
        activeNovaSessionId: state.activeNovaSessionId,
      }),
    },
  ),
);
